import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CircleCheck, Link2Off, Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoModeloCsv } from '../../componentes/importacao';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useContas } from './dados';
import { LancarDaLinha } from './LancarDaLinha';
import { ImportarExtrato } from './ImportarExtrato';
import { invalidarFinanceiro, type LinhaExtrato, type Pagina, type Sugestao } from './tipos';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { rotuloCategoria } from '../../nucleo/rotulos';
import { carregarMais } from '../../nucleo/foco';

type Status = 'pendente' | 'conciliada' | 'todas';

/** M4.4: importar extrato, linhas à esquerda, sugestões de par à direita, confirmação manual e desfazer. */
export function Conciliacao() {
  const { t } = useTranslation('financeiro');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const qc = useQueryClient();
  const contas = useContas(true);
  const [conta, setConta] = useState('');
  const [status, setStatus] = useState<Status>('pendente');
  const [selecionada, setSelecionada] = useState<LinhaExtrato | null>(null);
  const [importar, setImportar] = useState(false);
  const [lancar, setLancar] = useState(false);
  const podeConciliar = s.pode('financeiro.conciliar');
  const contaAtiva = conta || contas.data?.itens.find((c) => c.padrao)?.id || contas.data?.itens[0]?.id || '';

  const linhas = useInfiniteQuery({
    queryKey: ['/financeiro/extratos/linhas', contaAtiva, status],
    queryFn: ({ pageParam }) => get<Pagina<LinhaExtrato>>('/financeiro/extratos/linhas', { conta_id: contaAtiva, status, cursor: pageParam, limite: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
    enabled: !!contaAtiva,
  });
  const lista = linhas.data?.pages.flatMap((p) => p.itens) ?? [];
  // Depois de confirmar ou desfazer, a linha selecionada precisa refletir o estado novo.
  const atual = selecionada ? (lista.find((l) => l.id === selecionada.id) ?? selecionada) : null;
  useEffect(() => {
    setSelecionada(null);
  }, [contaAtiva, status]);

  const sugestoes = useQuery({
    queryKey: ['/financeiro/conciliacao/sugestoes', atual?.id],
    queryFn: () => get<Sugestao[]>('/financeiro/conciliacao/sugestoes', { linha_id: atual!.id }),
    enabled: !!atual && !atual.lancamento_id,
  });

  const confirmar = useMutation({
    mutationFn: (v: { linha_id: string; lancamento_id: string }) => post('/financeiro/conciliacao/confirmar', v),
    onSuccess: async () => {
      await invalidarFinanceiro(qc);
      setSelecionada(null);
      toast.sucesso(t('conciliacao.confirmada'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const desfazer = useMutation({
    mutationFn: (linha_id: string) => post('/financeiro/conciliacao/desfazer', { linha_id }),
    onSuccess: async () => {
      await invalidarFinanceiro(qc);
      toast.sucesso(t('conciliacao.desfeita'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {s.pode('financeiro.importar') && (
        <AcaoPrimaria>
          {/* Fase 2 (W6, L11): modelo do CSV do extrato (o OFX vem do banco). */}
          <BotaoModeloCsv caminho="/financeiro/extratos" />
          <Botao variante="primario" icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
            {t('acoes.importarExtrato')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-56 max-w-full">
          <Selecao aria-label={t('campos.conta')} opcoes={(contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))} value={contaAtiva} onChange={(e) => setConta(e.target.value)} />
        </div>
        <div role="group" aria-label={t('campos.conciliado')} className="inline-flex overflow-hidden rounded-lg border border-borda">
          {(['pendente', 'conciliada', 'todas'] as const).map((x) => (
            <button
              key={x}
              type="button"
              aria-pressed={status === x}
              onClick={() => setStatus(x)}
              className={`h-10 border-b-2 px-3 text-sm font-medium ${status === x ? 'border-acento-forte bg-acento-suave text-acento-forte' : 'border-transparent text-tinta-2 hover:bg-sup-2'}`}
            >
              {t(`conciliacao.status.${x}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <Cartao titulo={t('conciliacao.linhas')} semPadding>
          {contas.isPending ? (
            <Carregando />
          ) : contas.isError ? (
            <Erro erro={contas.error} aoTentar={() => contas.refetch()} />
          ) : !contaAtiva ? (
            <Vazio
              titulo={t('conciliacao.semContaTitulo')}
              texto={t('vazio.semContas')}
              acao={
                s.pode('financeiro.criar') ? (
                  <Link to="/cadastros/financeiro/contas" className="font-medium text-acento-forte hover:underline">
                    {t('conciliacao.irParaContas')}
                  </Link>
                ) : undefined
              }
            />
          ) : linhas.isPending ? (
            <Carregando />
          ) : linhas.isError ? (
            <Erro erro={linhas.error} aoTentar={() => linhas.refetch()} />
          ) : lista.length === 0 ? (
            <Vazio texto={status === 'pendente' ? t('vazio.linhasPendentes') : t('vazio.linhas')} />
          ) : (
            <>
              <ul className="max-h-96 divide-y divide-borda overflow-y-auto">
                {lista.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      aria-pressed={atual?.id === l.id}
                      onClick={() => setSelecionada(l)}
                      className={`flex w-full min-w-0 items-center gap-3 border-l-2 px-4 py-3 text-left hover:bg-sup-2 ${atual?.id === l.id ? 'border-acento-forte bg-sup-2' : 'border-transparent'}`}
                    >
                      <span className="numero w-20 shrink-0 text-xs text-tinta-3">{data(l.data)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-3 text-sm font-medium break-words sm:line-clamp-1" title={descricaoGerada(l.descricao)}>{descricaoGerada(l.descricao)}</span>
                        <span className="mt-0.5 block">
                          {l.lancamento_id ? <Selo cor="verde">{t('estado.conciliado')}</Selo> : l.sugestoes > 0 ? <Selo cor="ciano">{t('conciliacao.nSugestoes', { count: l.sugestoes })}</Selo> : <Selo>{t('conciliacao.semSugestao')}</Selo>}
                        </span>
                      </span>
                      <span className={`numero shrink-0 text-sm font-medium ${l.valor_centavos < 0 ? 'text-perigo' : 'text-verde'}`}>{dinheiro(l.valor_centavos)}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {linhas.hasNextPage && (
                <div className="flex justify-center border-t border-borda p-3">
                  <Botao variante="fantasma" carregando={linhas.isFetchingNextPage} onClick={(e) => carregarMais(() => linhas.fetchNextPage(), e.currentTarget)}>
                    {tn('comum.carregarMais')}
                  </Botao>
                </div>
              )}
            </>
          )}
        </Cartao>

        <Cartao titulo={t('conciliacao.sugestoes')} semPadding>
          {!atual ? (
            <Vazio titulo={t('conciliacao.escolhaLinha')} texto={t('conciliacao.escolhaLinhaTexto')} />
          ) : atual.lancamento_id ? (
            <div className="flex flex-col gap-4 p-4">
              <p className="flex items-center gap-2 text-sm font-medium text-verde">
                <CircleCheck className="size-4" aria-hidden />
                {t('conciliacao.conciliadaCom')}
              </p>
              <p className="rounded-lg border border-borda p-3 text-sm">{textoLivre(atual.lancamento_descricao)}</p>
              {podeConciliar && (
                <Botao className="w-fit" icone={<Link2Off className="size-4" />} carregando={desfazer.isPending} onClick={() => desfazer.mutate(atual.id)}>
                  {t('acoes.desfazerConciliacao')}
                </Botao>
              )}
            </div>
          ) : sugestoes.isPending ? (
            <Carregando />
          ) : sugestoes.isError ? (
            <Erro erro={sugestoes.error} aoTentar={() => sugestoes.refetch()} />
          ) : (
            <div className="flex flex-col gap-3 p-4">
              <p className="text-sm text-tinta-3">{t('conciliacao.regra')}</p>
              {sugestoes.data.length === 0 && <p className="text-sm text-tinta-2">{t('conciliacao.nenhumaSugestao')}</p>}
              <ul className="flex flex-col gap-2">
                {sugestoes.data.map((x) => (
                  <li key={x.id} className="flex min-w-0 flex-wrap items-center gap-3 rounded-lg border border-borda p-3">
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-3 text-sm font-medium break-words sm:line-clamp-1" title={descricaoGerada(x.descricao)}>{descricaoGerada(x.descricao)}</span>
                      <TextoCortado className="block text-xs text-tinta-3">
                        {[data(x.data_caixa), nomePessoa(x.parceiro_nome), rotuloCategoria(x.categoria_nome), x.diferenca_dias ? t('conciliacao.diasDeDiferenca', { count: x.diferenca_dias }) : t('conciliacao.mesmoDia')].filter(Boolean).join(', ')}
                      </TextoCortado>
                    </span>
                    <span className="numero shrink-0 text-sm font-medium">{anunciarOcultos(dinheiro(x.valor_centavos, x.moeda))}</span>
                    {podeConciliar && (
                      <Botao variante="primario" tamanho="sm" carregando={confirmar.isPending} onClick={() => confirmar.mutate({ linha_id: atual.id, lancamento_id: x.id })}>
                        {t('acoes.confirmar')}
                      </Botao>
                    )}
                  </li>
                ))}
              </ul>
              {s.pode('financeiro.criar') && podeConciliar && (
                <Botao className="w-fit" icone={<Plus className="size-4" />} onClick={() => setLancar(true)}>
                  {t('acoes.lancarEstaLinha')}
                </Botao>
              )}
            </div>
          )}
        </Cartao>
      </div>

      <ImportarExtrato aberto={importar} contaInicial={contaAtiva} aoFechar={() => setImportar(false)} />
      {/* Fase 2, W7 (B6): lançamento (ou documento no Contas) e conciliação numa operação só. */}
      <LancarDaLinha linha={lancar && atual && !atual.lancamento_id ? atual : null} aoFechar={() => (setLancar(false), setSelecionada(null))} />
    </div>
  );
}
